import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight, Globe, Moon, Asterisk } from "lucide-react";
import dynamic from "next/dynamic";
import nasa_logo from "../../../images/nasa_logo.png";
const Galaxy = dynamic(() => import("../../../components/Galaxy/index"), { ssr: false });
const sData = [
  {
    links: "mars",
    link: "mars",
    title: "Mars",
    count: `1827 regions`,
    price: 1,
    category: "Planets",
    id: "mars",
    glb: "https://moon.niftysky.org/mars",
  },
  {
    links: "moon",
    link: "moon",
    title: "Moon",
    count: `9014 regions`,
    price: 0.5,
    category: "Moons",
    id: "earthsmoon",
    glb: "https://moon.niftysky.org/earthsmoon",
  },
  {
    links: "Venus",
    link: "venus",
    title: "Venus",
    count: `1976 regions`,
    category: "Planets",
    price: 0.3,
    id: "venus",
    glb: "https://moon.niftysky.org/venus",
  },
  {
    links: "neo",
    link: "neo",
    title: "Near Earth Asteroids Registry",
    count: `2970 asteroids`,
    price: 0.25,
    category: "Asteroids",
    id: "eros",
    glb: "https://moon.niftysky.org/eros",
  },
  {
    links: "mercury",
    link: "mercury",
    title: "Mercury",
    count: `539 regions`,
    price: 0.4,
    category: "Planets",
    id: "mercury",
    glb: "https://planets.niftysky.org/mercury",
  },

  {
    links: "Titan",
    link: "titan",
    title: "Titan",
    price: 0.5,
    count: `284 regions`,
    category: "Moons",
    id: "titan",
    glb: "https://planets.niftysky.org/titan",
  },
  {
    links: "Pluto",
    link: "pluto",
    title: "Pluto",
    count: `52 regions`,
    price: 0.7,
    category: "Planets",
    id: "pluto",
    glb: "https://planets.niftysky.org/pluto",
  },
  {
    links: "Ganymede",
    link: "ganymede",
    title: "Ganymede",
    count: `196 regions`,
    price: 0.25,
    category: "Moons",
    id: "ganymede",
    glb: "https://planets.niftysky.org/ganymede",
  },
  {
    links: "Europa",
    link: "europa",
    title: "Europa",
    count: `121 regions`,
    price: 0.6,
    category: "Moons",
    id: "europa",
    glb: "https://planets.niftysky.org/europa",
  },
  {
    links: "exoplanets",
    link: "exoplanets",
    title: "Exoplanets",
    count: `4947 regions`,
    price: 0.5,
    category: "Planets",
    id: "cancri",
    glb: "https://planets.niftysky.org/cancri",
  },
  {
    links: "pha",
    link: "pha",
    title: "Potentially Hazardous Asteroids Registry",
    count: `772 regions`,
    price: 0.3,
    category: "Asteroids",
    id: "bennu",
    glb: "https://planets.niftysky.org/bennu",
  },
  {
    links: "rhea",
    link: "rhea",
    title: "Rhea",
    count: `143 regions`,
    price: 0.25,
    category: "Moons",
    id: "rhea",
    glb: "https://planets.niftysky.org/rhea",
  },
  {
    links: "callisto",
    link: "callisto",
    title: "Callisto",
    count: `154 regions`,
    price: 0.25,
    category: "Moons",
    id: "callisto",
    glb: "https://planets.niftysky.org/callisto",
  },
  {
    links: "io",
    link: "io",
    title: "Io",
    count: `224 regions`,
    price: 0.25,
    category: "Moons",
    id: "io",
    glb: "https://planets.niftysky.org/io",
  },
];

// Fallback component for ModelViewer loading
const ModelViewerFallback = () => (
  <div className="w-full h-[300px] bg-gradient-to-br from-gray-800/30 to-gray-900/30 flex items-center justify-center text-gray-300">
    Loading 3D Model...
  </div>
);

// Fallback poster image
const placeholderImage = "/images/placeholder-model.png"; // Place in public/images/

// Fallback local GLB file (download a sample .glb and place in public/models/)
const fallbackGlb = "/models/fallback.glb"; // Add a sample .glb file

// Dynamically import model-viewer
const ModelViewer = dynamic(
  () => import("@google/model-viewer").then((mod) => mod.default),
  { ssr: false, loading: () => <ModelViewerFallback /> }
);

// WebGL galaxy background — rendered opaque, so it cannot flash white.
const AnimatedGalaxy = () => (
  <div className="fixed inset-0 -z-10 pointer-events-none">
    <Galaxy
      density={1.2}
      glowIntensity={0.4}
      saturation={0.85}
      hueShift={220}
      mouseInteraction={false}
      mouseRepulsion={false}
    />
  </div>
);

const CategorySkeleton = () => (
  <div className="group relative bg-gradient-to-b from-gray-900/20 to-black/20 backdrop-blur-xl rounded-3xl border border-blue-900/20 overflow-hidden animate-pulse">
    <div className="relative">
      <div className="w-full h-64 bg-gradient-to-br from-gray-800/30 to-gray-900/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
    </div>
    <div className="p-6 space-y-4">
      <div className="h-5 bg-gray-700/50 rounded-lg" />
      <div className="flex items-center justify-between">
        <div className="h-4 bg-gray-700/30 rounded w-1/3" />
        <div className="h-4 bg-gray-700/30 rounded w-1/4" />
      </div>
    </div>
  </div>
);

export default function Category() {
  const router = useRouter();
  const { id } = router.query;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isClient, setIsClient] = useState(false);
  const [webGLError, setWebGLError] = useState(null);

  useEffect(() => {
    // Check WebGL support
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (!gl) {
      setWebGLError("WebGL is not supported in this browser.");
    }

    setIsClient(true);
    if (id) {
      setLoading(true);
      try {
        console.log("sData:", sData); // Debug sData
        const filteredData = sData.filter(
          (i) => i.category.toLowerCase() === id.toLowerCase()
        );
        console.log("Filtered Data:", filteredData); // Debug filtered data
        setData(filteredData);
      } catch (error) {
        console.error("Error filtering sData:", error);
      }
      setTimeout(() => setLoading(false), 1000); // Simulate loading
    }
  }, [id]);

  const getCategoryIcon = (category) => {
    switch (category.toLowerCase()) {
      case "planets":
        return <Globe className="w-5 h-5 text-blue-400" />;
      case "moons":
        return <Moon className="w-5 h-5 text-yellow-300" />;
      case "asteroids":
        return <Asterisk className="w-5 h-5 text-purple-300" />;
      default:
        return <Globe className="w-5 h-5 text-blue-400" />;
    }
  };

  const handleModelError = (event) => {
    console.error(`Error loading model: ${event.target.src}`, event);
    // Fallback to local model if external URL fails
    event.target.src = fallbackGlb;
  };

  if (!isClient) return null; // Prevent SSR rendering

  if (webGLError) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black to-gray-900 flex items-center justify-center text-white">
        <p>{webGLError}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black to-gray-900 relative overflow-hidden">
      <AnimatedGalaxy />
      <div className="relative z-10">
        <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 text-white">
          <h1
            className="text-4xl sm:text-5xl font-extrabold text-center bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-blue-400 animate-in"
            style={{ textTransform: "capitalize" }}
          >
            {id || "Categories"}
          </h1>
          <div className="mt-12">
            {loading ? (
              <div className="grid grid-cols-2 gap-8">
                {Array.from({ length: 4 }).map((_, i) => (
                  <CategorySkeleton key={i} />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-8">
                {data.length === 0 ? (
                  <div className="col-span-2 text-center text-gray-300">
                    No items found for category "{id}".
                  </div>
                ) : (
                  data.map(
                    ({ title, link, count, price, glb, category }, index) => (
                      <div
                        key={index}
                        onClick={() => router.push(`/real-estate/${link}`)}
                        className="group relative bg-gradient-to-b from-gray-900/30 to-black/30 backdrop-blur-2xl rounded-3xl border border-blue-900/20 overflow-hidden hover:border-sky-500/40 hover:-translate-y-1 hover:shadow-2xl hover:shadow-sky-500/20 transition-all duration-500"
                        style={{ animationDelay: `${index * 100}ms` }}
                      >
                        <Link
                          href={`/real-estate/${link}`}
                          aria-label={`View ${title} details`}
                        >
                          <div className="relative overflow-hidden">
                            {glb ? (
                              <ModelViewer
                                src={glb}
                                poster={placeholderImage}
                                style={{
                                  width: "100%",
                                  height: "300px",
                                  background: "transparent",
                                  display: "block",
                                }}
                                camera-controls
                                auto-rotate
                                auto-rotate-delay="0"
                                rotation-per-second="30deg"
                                ar
                                alt={`${title} 3D Model`}
                                loading="lazy"
                                shadow-intensity="1"
                                environment-image="neutral"
                                exposure="1"
                                onError={handleModelError}
                                className="group-hover:scale-105 transition-transform duration-700 ease-out"
                              />
                            ) : (
                              <div className="w-full h-[300px] bg-gradient-to-br from-gray-800/30 to-gray-900/30 flex items-center justify-center text-gray-300">
                                No model available
                              </div>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-70 group-hover:opacity-50 transition-opacity duration-500" />
                            <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1 bg-blue-600/30 backdrop-blur-md rounded-full text-sky-200 text-xs font-medium border border-blue-400/40 shadow-md">
                              {getCategoryIcon(category)}
                              {category}
                            </div>
                          </div>
                          <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                              <h3 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-blue-400 group-hover:text-sky-200 transition-colors duration-300">
                                {title}
                                <ArrowRight className="inline-block w-6 h-6 ml-2 group-hover:scale-125 transition-transform duration-300" />
                              </h3>
                            </div>
                            <div className="flex items-center justify-between text-white">
                              <div className="flex items-center gap-2">
                                <Globe className="w-4 h-4 text-sky-300" />
                                <span className="text-sm font-medium">
                                  {count}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-bold text-yellow-300">
                                  {price} ETH
                                </span>
                                <Image
                                  src="/images/eth.png"
                                  width={20}
                                  height={20}
                                  alt="ETH"
                                  className="bg-transparent"
                                />
                              </div>
                            </div>
                            <div className="flex items-center justify-between mt-4">
                              <p className="text-sm text-gray-300">
                                NASA Visualization Technology
                              </p>
                              <div className="bg-gray-800/50 border border-sky-500/20 rounded-full p-1 shadow-sky-500/20">
                                <Image
                                  src={nasa_logo.src}
                                  alt="NASA Logo"
                                  width={30}
                                  height={30}
                                />
                              </div>
                            </div>
                          </div>
                        </Link>
                      </div>
                    )
                  )
                )}
              </div>
            )}
          </div>
        </section>
      </div>
      <style jsx>{`
        @keyframes moveStarRight {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(calc(100vw + 20px));
          }
        }
        @keyframes moveStarLeft {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(calc(-100vw - 20px));
          }
        }
        @keyframes animate-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-in {
          animation: animate-in 0.5s ease-out;
        }
      `}</style>
    </div>
  );
}
